import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import ErrorBanner from '@avtechsoftware/react-component-library/src/components/common/ErrorBanner';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { faCog, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from 'react-router-dom';
import ModalComponent from '../../../../layout/Modals/ModalComponent/ModalComponent';
import { connectModalContainerContextToComponent } from '../../../../../contexts/ModalContext/ModalContext';
import styles from './ApplyTagsModal.module.css';
import { createTag, editTags, selectTagList } from '../../../../../redux/slices/tagListSlice/tagListSlice';
import IndeterminateCheckbox from '../../../../common/IndeterminateCheckbox/IndeterminateCheckbox';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';
import { RaaRoles } from '../../../../../utility/SecurityRules';
import HasRolePrivileges from '../../../../highorder/HasRolePrivileges';

const associationCheckBoxStateMap = {
  ALL: 'true',
  SOME: 'indeterminate',
  NONE: 'false',
};

function ApplyTagsModal({
  selectedIdList,
  tagsAssociatedWithSome,
  tagsAssociatedWithAll,
  setModalJSX,
  entityType,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const tagList = useSelector(selectTagList);
  const { activeSubscription } = useSelector(selectSessionInformation);

  const [show, setShow] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tags, setTags] = useState([]);
  const [isSearchedTextHovered, setIsSearchedTextHovered] = useState(false);
  const [tagIdsToApply, setTagIdsToApply] = useState([]);
  const [tagIdsToRemove, setTagIdsToRemove] = useState([]);

  const displaySearchText = searchText ? (
    <b>
      &#34;
      {searchText}
      &#34;
    </b>
  ) : '';

  /* istanbul ignore next */
  const filteredTags = tags.filter(
    (tag) => tag.name.toLowerCase().startsWith(searchText.toLowerCase()),
  );

  // eslint-disable-next-line max-len
  const getExistingTagByDisplayName = tags.find((tag) => tag.name.toLowerCase() === searchText.toLowerCase());

  const closeModalHandler = () => {
    setShow(false);
    setModalJSX((<></>));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const tagPatchData = {
      entityType,
      entityIdList: selectedIdList,
      tagIdList: tagIdsToApply,
      removedTagIdList: tagIdsToRemove,
    };
    try {
      await dispatch(editTags(tagPatchData)).unwrap();
      setSubmitting(false);
      closeModalHandler();
    } catch (e) {
      setSubmitting(false);
      setError('Unexpected error applying tags. Please try again.');
    }
  };

  const handleApplyTag = (tagId) => {
    if (!tagIdsToApply.includes(tagId)) {
      setTagIdsToApply([...tagIdsToApply, tagId]);
    }
    if (tagIdsToRemove.includes(tagId)) {
      tagIdsToRemove.filter((id) => id !== tagId);
    }
  };

  const handleRemoveTag = (tagId) => {
    if (!tagIdsToRemove.includes(tagId)) {
      setTagIdsToRemove([...tagIdsToRemove, tagId]);
    }
    if (tagIdsToApply.includes(tagId)) {
      const updatedTagsToApply = tagIdsToApply.filter((id) => id !== tagId);
      setTagIdsToApply(updatedTagsToApply);
    }
  };

  const handleUpdateTag = (tagToUpdate) => {
    const updatedTag = tagToUpdate;
    const prevAssociationState = tagToUpdate.associationCheckboxState;
    if (prevAssociationState === associationCheckBoxStateMap.ALL) {
      updatedTag.associationCheckboxState = associationCheckBoxStateMap.NONE;
      handleRemoveTag(tagToUpdate.id);
    }
    if (prevAssociationState === associationCheckBoxStateMap.SOME) {
      updatedTag.associationCheckboxState = associationCheckBoxStateMap.ALL;
      handleApplyTag(tagToUpdate.id);
    }
    if (prevAssociationState === associationCheckBoxStateMap.NONE) {
      updatedTag.associationCheckboxState = associationCheckBoxStateMap.ALL;
      handleApplyTag(tagToUpdate.id);
    }
    return updatedTag;
  };

  const handleToggleTagAssociationCheckboxState = (tagToUpdate) => {
    const updatedTag = handleUpdateTag(tagToUpdate);
    const updatedTags = tags.map((tag) => (tag.id === tagToUpdate.id ? updatedTag : tag));
    setTags(updatedTags);
  };

  const handleApplyNewTag = (newTagId, newTagData) => {
    const newTag = {
      id: newTagId,
      name: newTagData.tag.name,
      subscriptionId: newTagData.subscriptionId,
    };
    newTag.associationCheckboxState = associationCheckBoxStateMap.ALL;
    setTags([...tags, newTag]);
    handleApplyTag(newTagId);
  };

  const handleCreateNewTag = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    if (searchText.length < 1 || searchText.length > 100) {
      setError('Tag must be between 1 and 100 characters long.');
      setSubmitting(false);
      return false;
    }
    const tagData = {
      subscriptionId: activeSubscription.id,
      tag: {
        name: searchText,
        alertTagList: [],
        deviceTagList: [],
      },
    };
    try {
      const { id } = await dispatch(createTag(tagData)).unwrap();
      handleApplyNewTag(id, tagData);
      setSubmitting(false);
      setSearchText('');
    } catch (err) {
      setError(err.data[0] ? err.data[0].message : 'Unexpected error saving new tag. Please try again.');
      setSubmitting(false);
    }
    return true;
  };

  const getMappedTags = () => tagList.map((tag) => {
    if (tagsAssociatedWithAll.includes(tag.id)) {
      return ({ ...tag, associationCheckboxState: associationCheckBoxStateMap.ALL });
    }
    if (tagsAssociatedWithSome.includes(tag.id)) {
      return ({ ...tag, associationCheckboxState: associationCheckBoxStateMap.SOME });
    }
    return ({ ...tag, associationCheckboxState: associationCheckBoxStateMap.NONE });
  });

  useEffect(() => {
    setTags(getMappedTags());
  }, []);

  /* istanbul ignore next */
  return (
    <ModalComponent
      showConfirmCallToAction
      show={show}
      close={closeModalHandler}
      confirmButtonText="Apply Changes"
      confirmButtonAction={handleSubmit}
      isBtnDisabled={submitting}
    >
      <Modal.Header closeButton={false}>
        <Modal.Title className="w-100">
          <div className="clearfix">
            <div className="float-left">Update Tags</div>
            <div className="float-right">
              <Button
                variant="link"
                onClick={() => {
                  closeModalHandler();
                  history.push('/s/guidance/tags');
                }}
                className="text-muted"
                style={{ textDecoration: 'none' }}
              >
                Manage
                {' '}
                <FontAwesomeIcon icon={faCog} size="lg" />
              </Button>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ padding: '1rem 5rem' }}>
          <form onSubmit={handleCreateNewTag}>
            {error && (
            <div className="row">
              <div className="col">
                <ErrorBanner onCloseError={() => setError('')}>
                  {error}
                </ErrorBanner>
              </div>
            </div>
            )}
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <div className={styles.textInputContainer}>
                <input
                  className={`${styles.textInputStyle} customized-text`}
                  name="tags-search"
                  aria-label="tags-search"
                  onChange={(e) => setSearchText(e.target.value)}
                  value={searchText}
                />
                <div className={styles.inputIconStyle}>
                  <FontAwesomeIcon icon={faSearch} />
                </div>
              </div>
            </div>

            <div className={styles.tagContainer}>
              {
                filteredTags.map((tag, index) => (
                  <div className="form-group row" key={`${tag.name}-${index}`}>
                    <div className="form-check form-check-inline">
                      <IndeterminateCheckbox
                        onChange={() => handleToggleTagAssociationCheckboxState(tag)}
                        checked={tag.associationCheckboxState}
                        label={tag.name}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className="form-check-label text-nowrap"
                        htmlFor="inlineCheckbox1"
                      >
                        {tag.name}
                      </label>
                    </div>
                  </div>
                ))
              }
            </div>

            <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
              <div className="mt-2 mb-1">
                {
                searchText && (
                <button
                  type="submit"
                  className="btn btn-link"
                  aria-label="submit-new-tag"
                  onClick={handleCreateNewTag}
                  data-testId="submit-new-tag"
                  disabled={getExistingTagByDisplayName}
                  style={{ color: isSearchedTextHovered ? '#007bff' : 'black', textDecoration: 'none' }}
                  onMouseEnter={() => setIsSearchedTextHovered(!getExistingTagByDisplayName)}
                  onMouseLeave={() => setIsSearchedTextHovered(false)}
                >
                  <span>
                    {filteredTags.length === 0 ? 'Create tag ' : ''}
                    {displaySearchText}
                  </span>
                </button>
                )
              }
              </div>
            </HasRolePrivileges>
          </form>
        </div>
      </Modal.Body>
    </ModalComponent>
  );
}

ApplyTagsModal.propTypes = {
  selectedIdList: PropTypes.arrayOf(PropTypes.number).isRequired,
  setModalJSX: PropTypes.func.isRequired,
  tagsAssociatedWithSome: PropTypes.arrayOf(PropTypes.number).isRequired,
  tagsAssociatedWithAll: PropTypes.arrayOf(PropTypes.number).isRequired,
  entityType: PropTypes.number.isRequired,
};
export { ApplyTagsModal };
export default connectModalContainerContextToComponent(ApplyTagsModal);
